import React from "react";
import PropTypes from "prop-types";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { getValueFormatter, getMinMaxRange } from "../utils/formatting";
import { exportToCsv, downloadImage } from "../utils/exporters";
import { startCase, get } from "lodash-es";
import { numberWithCommas } from "../utils/formatting";
import { defaultColors, defaultActions } from "../utils/visualization";
import { Message, ChartContainer as DefaultChartContainer, useTectonicContext, } from "../components";
export const DonutChart = ({ exportFilename = "export.csv", status = { success: true }, data = [], colors = defaultColors, chartContainer: ChartContainer = DefaultChartContainer, enabledControls = ["actions"], labelFormatter = (label) => {
    return startCase(label.toString().toLowerCase());
}, valueFormatter = (value) => {
    return numberWithCommas(value);
}, labelField = "key", valueField = "count", height = 400, limit, percent, precision, title, titleAlign, colorFn, }) => {
    const ctx = useTectonicContext();
    const svgChartRef = React.useRef(null);
    let trimmedData = data;
    if (limit) {
        const other = { key: "Other", count: 0, value: 0 };
        data.slice(limit - 1).forEach((item) => {
            other.count += item.count;
            other.value += item.value;
        });
        trimmedData = data.slice(0, limit - 1);
        if (data.length > limit) {
            trimmedData.push(other);
        }
    }
    const _colors = colors === defaultColors
        ? [ctx === null || ctx === void 0 ? void 0 : ctx.primaryColor, ...defaultColors].filter(Boolean)
        : colors;
    let total = 0;
    data.forEach((item) => {
        total += get(item, valueField);
    });
    const noData = !trimmedData.length;
    if (noData) {
        trimmedData = [{ key: "No Data", count: 1, value: 0 }];
    }
    const handleDownloadImage = async (ref) => {
        if (ref && ref.container) {
            let svg = ref.container.children[0];
            await downloadImage(svg, ref.container.clientWidth, ref.container.clientHeight, "chart.png");
        }
    };
    function handleAction(action) {
        const getValue = (item) => item[valueField];
        if (action === "download-image") {
            handleDownloadImage(svgChartRef.current);
        }
        else if (action === "export-data") {
            exportToCsv([`Name`, "Value", "Percentage"], trimmedData.map((row) => [
                labelFormatter(get(row, labelField)),
                getValue(row),
                `${Math.round((getValue(row) / total) * 100)}%`,
            ]), exportFilename);
        }
    }
    const _valueFormatter = valueFormatter || getValueFormatter(getMinMaxRange(data, valueField));
    return (React.createElement(ChartContainer, { title: title, enabledControls: enabledControls, titleAlign: titleAlign, height: height, actions: defaultActions, onActionChange: handleAction },
        status.success && noData && (React.createElement(Message, { centered: true }, "No data available for this time period")),
        status.loading && React.createElement(Message, { centered: true }, "Loading..."),
        status.error && (React.createElement(Message, { error: true, centered: true }, status.error.message)),
        React.createElement(ResponsiveContainer, null,
            React.createElement(PieChart, { ref: svgChartRef, data: trimmedData, key: status.success + "" },
                React.createElement(Pie, { data: trimmedData, innerRadius: Math.round(height * 0.2), outerRadius: Math.round(height * 0.3), fill: "#8884d8", paddingAngle: 5, nameKey: labelField, dataKey: valueField }, trimmedData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: colorFn
                        ? colorFn(entry, index)
                        : _colors[index % _colors.length] })))),
                React.createElement(Legend, { height: 30, formatter: (value) => labelFormatter(value) }),
                !noData && (React.createElement(Tooltip, { formatter: (value, name) => {
                        let label = labelFormatter(name);
                        if (percent) {
                            if (precision) {
                                return [
                                    `${_valueFormatter(Math.round((value / total) * (10 * precision) * 100) /
                                        (10 * precision))}%`,
                                    label,
                                ];
                            }
                            else {
                                return [
                                    `${_valueFormatter(Math.round((value / total) * 100))}%`,
                                    label,
                                ];
                            }
                        }
                        return [
                            _valueFormatter(value),
                            labelFormatter(name),
                        ];
                    } }))))));
};
DonutChart.propTypes = {
    title: PropTypes.node,
    status: PropTypes.object,
    limit: PropTypes.number,
    labelFormatter: PropTypes.func,
    valueFormatter: PropTypes.func,
    valueField: PropTypes.string,
    labelField: PropTypes.string,
    percent: PropTypes.bool,
    precision: PropTypes.number,
    colorFn: PropTypes.func,
    data: PropTypes.array,
    colors: PropTypes.arrayOf(PropTypes.string),
    chartContainer: PropTypes.elementType,
    enabledControls: PropTypes.arrayOf(PropTypes.oneOf(["actions"])),
    exportFilename: PropTypes.string,
};
//# sourceMappingURL=DonutChart.js.map